import React from 'react';
import {
  ILegalTransaction,
  ILegalTransactionDataCollection,
} from '../Interfaces/ILegalTransaction';
import { IArnoTopObject } from '../Interfaces/IArnoTopObject';
import { ILegalTransactionType } from '../Interfaces/ILegalTransactionType';
import {
  Alert,
  Box,
  Card,
  CardContent,
  FormControlLabel,
  FormGroup,
  Grid,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { ILegalTransactionRelationship } from '../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationship';
import { ILegalClient } from '../Interfaces/ILegalClient';
import {
  dataCollectionTitle,
  issueTitle,
} from './LegalTransactionRelationships/LegalTransactionDataCollection';
import { LegalTransactionRelationshipTypeEnum } from '../Interfaces/ILegalTransactionRelationship/LegalTransactionRelationshipTypeEnum';

interface IProps {
  legalTransactionObject: ILegalTransaction;
  setLegalTransactionObject: Function;
  relationships: ILegalTransactionRelationship[];
  setRelationships: React.Dispatch<
    React.SetStateAction<ILegalTransactionRelationship[]>
  >;
  dataCollection: ILegalTransactionDataCollection[];
  setDataCollection: React.Dispatch<
    React.SetStateAction<ILegalTransactionDataCollection[]>
  >;
  takenLegalClients: ILegalClient[];
  arnotopObjects?: IArnoTopObject[];
  legalTransactionTypes: ILegalTransactionType[];
}

export const LegalTransactionArnoTop: React.FC<IProps> = ({
  legalTransactionObject,
  setLegalTransactionObject,
  relationships,
  setRelationships,
  dataCollection,
  setDataCollection,
  takenLegalClients,
  arnotopObjects,
  legalTransactionTypes,
}) => {
  const isEnabled =
    legalTransactionObject.syncWithArnoTop ||
    dataCollection.find(dc => dc.syncWithArnoTop) !== undefined ||
    relationships.find(rel => rel.syncWithArnoTop) !== undefined;
  const isSyncAllLegalClients = takenLegalClients.every(
    lc => Boolean(lc.syncWithArnoTop) === true
  );
  const currentType = legalTransactionTypes.find(
    x =>
      x.idLegalTransactionType === legalTransactionObject.idLegalTransactionType
  );
  //
  const getCurrentSyncObject = () => {
    if (dataCollection.find(dc => dc.syncWithArnoTop)) {
      const currentObject = dataCollection.find(dc => dc.syncWithArnoTop);
      if ('idCompanyFormation' in currentObject!) {
        return `cf-${currentObject!.idCompanyFormation}`;
      } else if ('idInherit' in currentObject!) {
        return `inh-${currentObject!.idInherit}`;
      } else if ('idMortgage' in currentObject!) {
        return `mor-${currentObject!.idMortgage}`;
      } else {
        return '';
      }
    } else if (relationships.find(rel => rel.syncWithArnoTop)) {
      return `rel-${relationships.find(rel => rel.syncWithArnoTop)?.idRelationship}`;
    } else if (legalTransactionObject.syncWithArnoTop) {
      return 'v';
    } else {
      return '';
    }
  };

  const setCurrentSyncObject = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    if (value === 'v') {
      setDataCollection([
        ...dataCollection.map(dc => {
          return {
            ...dc,
            syncWithArnoTop: false,
          };
        }),
      ]);
      setRelationships([
        ...relationships.map(rel => {
          return {
            ...rel,
            syncWithArnoTop: false,
          };
        }),
      ]);
    } else if (value.includes('rel')) {
      const splittetValue = value.split('-');
      const idRelationsship = Number(splittetValue[1]);

      setDataCollection([
        ...dataCollection.map(dc => {
          return {
            ...dc,
            syncWithArnoTop: false,
          };
        }),
      ]);
      setRelationships([
        ...relationships.map(rel => {
          return {
            ...rel,
            syncWithArnoTop: rel.idRelationship === idRelationsship,
          };
        }),
      ]);
    } else {
      const splittetValue = value.split('-');
      if (splittetValue.length === 2) {
        const dataCollectionType = splittetValue[0];
        const idDataCollection = Number(splittetValue[1]);

        setDataCollection([
          ...dataCollection.map(dc => {
            return {
              ...dc,
              syncWithArnoTop:
                (dataCollectionType === 'cf' &&
                  'idCompanyFormation' in dc &&
                  dc.idCompanyFormation === idDataCollection) ||
                (dataCollectionType === 'inh' &&
                  'idInherit' in dc &&
                  dc.idInherit === idDataCollection) ||
                (dataCollectionType === 'mor' &&
                  'idMortage' in dc &&
                  dc.idMortage === idDataCollection),
            };
          }),
        ]);
        setRelationships([
          ...relationships.map(rel => {
            return {
              ...rel,
              syncWithArnoTop: false,
            };
          }),
        ]);
      }
    }
  };

  if (!arnotopObjects) {
    return (
      <Box sx={{ mt: 3, mb: 3 }}>
        <Alert severity='warning'>
          ArnoTop-Gegestände konnten nicht bezogen werden!
        </Alert>
      </Box>
    );
  }

  return (
    <>
      <Card variant='outlined' sx={{ mt: 3, mb: 3 }}>
        <CardContent>
          <Typography>ArnoTop-Synchronisation</Typography>
          <Grid container>
            <Grid xs={5}>
              <FormGroup row>
                <FormControlLabel
                  control={
                    <Switch
                      checked={isEnabled}
                      onChange={(
                        event: React.ChangeEvent<HTMLInputElement>
                      ) => {
                        setLegalTransactionObject({
                          ...legalTransactionObject,
                          syncWithArnoTop: event.target.checked,
                          idArnoTopObject: !event.target.checked
                            ? null
                            : currentType?.idArnoTopObject == undefined
                              ? null
                              : currentType?.idArnoTopObject,
                        });
                        if (!event.target.checked) {
                          setDataCollection([
                            ...dataCollection.map(dc => {
                              return {
                                ...dc,
                                syncWithArnoTop: false,
                              };
                            }),
                          ]);
                          setRelationships([
                            ...relationships.map(rel => {
                              return {
                                ...rel,
                                syncWithArnoTop: false,
                              };
                            }),
                          ]);
                        }
                      }}
                    />
                  }
                  label='Synchronisation an ArnoTop'
                />
                <FormControlLabel
                  title={
                    isSyncAllLegalClients ||
                    takenLegalClients.every(
                      lc => Boolean(lc.syncWithArnoTop) === false
                    )
                      ? 'Mandanten an ArnoTop'
                      : 'Nicht alle Mandanten sind zum Sync aktiviert!'
                  }
                  control={
                    <Switch
                      color={
                        isSyncAllLegalClients ||
                        takenLegalClients.every(
                          lc => Boolean(lc.syncWithArnoTop) === false
                        )
                          ? 'default'
                          : 'warning'
                      }
                      disabled={isSyncAllLegalClients}
                      checked={
                        isSyncAllLegalClients ||
                        legalTransactionObject.syncAllLegalClientsWithArnotop
                      }
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        setLegalTransactionObject({
                          ...legalTransactionObject,
                          syncAllLegalClientsWithArnotop: event.target.checked,
                        })
                      }
                    />
                  }
                  label='Mandanten an ArnoTop'
                />
              </FormGroup>
            </Grid>
            <Grid xs={3}>
              <TextField
                //type="email"
                variant='outlined'
                label='ArnoTop Gegenstand'
                fullWidth
                value={
                  legalTransactionObject.idArnoTopObject === null
                    ? ''
                    : legalTransactionObject.idArnoTopObject
                }
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  setLegalTransactionObject({
                    ...legalTransactionObject,
                    idArnoTopObject: Number(event.target.value),
                  })
                }
                required={isEnabled}
                select
                size='small'
              >
                {arnotopObjects
                  .sort((a, b) => (a.ArnoTopObject < b.ArnoTopObject ? -1 : 1))
                  .map(arno => (
                    <MenuItem
                      key={`arnotop-object-${arno.idArnoTopObject}`}
                      value={arno.idArnoTopObject}
                    >
                      {arno.ArnoTopObject}
                    </MenuItem>
                  ))}
              </TextField>
            </Grid>
            <Grid xs={3}>
              <TextField
                //type="email"
                variant='outlined'
                label='Objekt'
                fullWidth
                value={getCurrentSyncObject()}
                onChange={setCurrentSyncObject}
                required={isEnabled}
                select
                size='small'
              >
                <MenuItem key={`v`} value={`v`}>
                  Vorgang
                </MenuItem>
                {relationships
                  .filter(
                    rel =>
                      rel.idRelationshipType ===
                      LegalTransactionRelationshipTypeEnum.PurchaseContract
                  )
                  .map(rel => (
                    <MenuItem
                      key={`rel-${rel.idRelationship}`}
                      value={`rel-${rel.idRelationship}`}
                    >
                      {issueTitle(rel)}
                    </MenuItem>
                  ))}
                {dataCollection.map(dc => {
                  if ('idCompanyFormation' in dc) {
                    return (
                      <MenuItem
                        key={`cf-${dc.idCompanyFormation}`}
                        value={`cf-${dc.idCompanyFormation}`}
                      >
                        {dataCollectionTitle(dc)}
                      </MenuItem>
                    );
                  } else if ('idInherit' in dc) {
                    return (
                      <MenuItem
                        key={`inh-${dc.idInherit}`}
                        value={`inh-${dc.idInherit}`}
                      >
                        {dataCollectionTitle(dc)}
                      </MenuItem>
                    );
                  } else if ('idMortgage' in dc) {
                    return (
                      <MenuItem
                        key={`inh-${dc.idMortgage}`}
                        value={`inh-${dc.idMortgage}`}
                      >
                        {dataCollectionTitle(dc)}
                      </MenuItem>
                    );
                  }
                })}
              </TextField>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};
